/**
 * `SegmentedControl` — a generic radiogroup segmented control with an animated
 * active pill, generalized from App.tsx's `WindowSelect` so the detail modal's
 * bar/graph toggle and window selector reuse the SAME sanctioned pattern
 * (DESIGN.md: no new toggle idiom without sign-off). The page's `WindowSelect`
 * is intentionally left as-is to avoid touching working code.
 *
 * Each instance MUST pass a unique `layoutId` — framer-motion shares layout
 * animations across same-id `motion` elements, so a duplicate would make the
 * page's window pill animate into the modal's.
 */
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRef } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  /** Unique across the whole page — see file header. */
  layoutId: string;
  ariaLabel: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  layoutId,
  ariaLabel,
}: SegmentedControlProps<T>) {
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // ARIA radiogroup keyboard model: arrow keys move selection + focus
  // (wrap-around), Home/End jump to the ends. Selection follows focus, as
  // the WAI-ARIA radio pattern prescribes.
  const moveTo = (targetIdx: number) => {
    // targetIdx is always in range — every caller derives it via `% length`
    // or the 0 / length-1 ends, and handleKeyDown returns early when empty.
    onChange(options[targetIdx].value);
    btnRefs.current[targetIdx]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (options.length === 0) return;
    const i = options.findIndex((o) => o.value === value);
    const current = i === -1 ? 0 : i;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        moveTo((current + 1) % options.length);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        moveTo((current - 1 + options.length) % options.length);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(0);
        break;
      case 'End':
        e.preventDefault();
        moveTo(options.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="relative inline-flex items-center gap-1 rounded-full bg-[rgb(var(--control))] p-1"
    >
      {options.map((opt, idx) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              btnRefs.current[idx] = el;
            }}
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            type="button"
            onClick={() => {
              onChange(opt.value);
            }}
            onKeyDown={handleKeyDown}
            className={clsx(
              'relative rounded-full px-3 py-1 text-[13px] font-semibold leading-6 transition-colors duration-150',
              active ? 'text-ink-1' : 'text-ink-2 hover:text-ink-1',
            )}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 z-0 rounded-full bg-[rgb(var(--control-active))]"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                aria-hidden
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
